import React from 'react';
import * as enums from '@local/finsera-core/src/enums';
import { csv } from '@local/finsera-core/src/helpers';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { useUiStyles } from '../../hooks';

import { ColumnWidth, FinColumnsPicker, FinDensityFilter, FinFilterTrigger } from '..';

type ToolbarProps = {
  hideFilters?: boolean;
  hideDensity?: boolean;
  hideDataCount?: boolean;
  hideColumnWidth?: boolean;
  hideDownload?: boolean;
  hideColumnPicker?: boolean;
  customToolbar?: (_props: { columns: string[] }) => React.ReactNode;
  fixedWidth?: types.components.dataGrid.TableParams['fixed_width'];
  setFixedWidth?: (_v: types.components.dataGrid.TableParams['fixed_width']) => void;

  alert?: types.common.Alert;
  responseColumns?: React.MutableRefObject<string[]>;
  downloading?: boolean;
  loading?: boolean;
  filteredRows?: Record<string, any>[];
  mappedColumns?: any[];
};

const Toolbar = ({
  alert,
  fixedWidth,
  setFixedWidth,
  responseColumns,
  downloading,
  filteredRows,
  mappedColumns,
  customToolbar,
  hideFilters,
  hideDensity,
  hideColumnWidth,
  hideDownload,
  hideColumnPicker,
}: ToolbarProps) => {
  const theme = mui.styles.useTheme() as mui.core.Theme;
  const uiStyles = useUiStyles();

  const errorMessage = React.useMemo(() => {
    const defaultMsg = 'Something went wrong. Please try again later.';
    if (typeof alert?.message === 'string') return alert.message || defaultMsg;
    return defaultMsg;
  }, [alert]);

  const handleLocalDownload = () => {
    try {
      const csvData = csv.toCsv(filteredRows, mappedColumns, enums.SEPARATORS_ENUM.COMMA, false, true).join('');
      // Create a Blob from the CSV data
      const blob = new Blob([csvData], { type: 'text/csv' });

      // Create a link element
      const link = document.createElement('a');

      // Set the link's properties
      link.href = URL.createObjectURL(blob);
      link.download = 'data.csv';

      // Append the link to the document
      document.body.appendChild(link);

      // Trigger a click event on the link
      link.click();

      // Remove the link from the document
      document.body.removeChild(link);
    } catch {
      console.log('Unable to dowlonad!');
    }
  };

  return (
    <mui.core.Box sx={{ width: '100%' }}>
      <mui.core.Box display="flex" flex={1} mb={2} p={2} alignItems="center">
        {!_.isEmpty(alert) && (
          <mui.core.Box mr={2} display="flex" alignItems="center" sx={uiStyles.hidePrint}>
            <mui.core.Tooltip title={errorMessage} arrow>
              <mui.core.Chip
                color={alert.severity == 'warning' ? 'warning' : 'error'}
                label={alert.severity == 'warning' ? errorMessage : 'Failed'}
                icon={<mui.icons.ErrorRounded sx={{ fontSize: '0.9rem' }} />}
              />
            </mui.core.Tooltip>
          </mui.core.Box>
        )}

        <mui.core.Box flexGrow={1}>
          <mui.core.Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {customToolbar &&
              React.createElement(customToolbar, {
                columns: responseColumns?.current ?? [],
              })}
          </mui.core.Box>
        </mui.core.Box>

        <mui.core.Box
          pl={2}
          display="flex"
          sx={{
            ...uiStyles.hidePrint,
            button: {
              border: `1px solid ${theme.palette.primaryLight.light}`,
              ml: 1,
            },
            svg: { fontSize: '14px!important' },
          }}
        >
          <mui.core.Stack flexDirection="row" ml={3}>
            {!hideDownload && (
              <mui.core.Box
                sx={{
                  '& .MuiButton-root': { border: '0 !important', padding: '8px 4px' },
                  '& svg': { display: 'none' },
                }}
              >
                <mui.dataGrid.GridToolbarExportContainer>
                  <mui.core.MenuItem disabled={downloading} onClick={() => handleLocalDownload()}>
                    Download as CSV
                  </mui.core.MenuItem>
                </mui.dataGrid.GridToolbarExportContainer>
              </mui.core.Box>
            )}
            {!hideColumnPicker && <FinColumnsPicker />}
            {!hideDensity && <FinDensityFilter />}
            {!hideFilters && <FinFilterTrigger />}
            {!hideColumnWidth && <ColumnWidth fixedWidth={fixedWidth} setFixedWidth={setFixedWidth} />}
          </mui.core.Stack>
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Toolbar;
